/**
 * ContentPlanWizardStrip — M2 guided-flow layer for ContentPlansPage.
 *
 * A collapsible horizontal 9-step progress spine rendered ABOVE the existing
 * 3-tab shell (D7: a layer, not a rebuild). Step state comes EXCLUSIVELY from
 * the D12 flow-readiness contract via `useContentPlanFlowReadiness` (parity
 * with `content-plan doctor` by construction).
 *
 * Navigation contract (regression-pinned):
 *   - the wizard annotates, the user navigates: clicking a step only SELECTS
 *     its card; ONLY the explicit "Open <sub-tab>" link / nav CTAs change the
 *     sub-tab (same `update()` call a user tab click performs);
 *   - locked steps stay visible, disabled, annotated with the unlock reason
 *     and a link that selects the unlocking step;
 *   - completion badges reflect real output existence, never "visited".
 *
 * Also hosts: the persistent TaskProgressBanner (LT1), the M3 Run-all entry
 * (confirm dialog + staged in-page panel + review checkpoints), and the
 * wizard-owned GenerateTopicsDialog.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Paper, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { CONTENT_PLAN_BACKLOG_READY_THRESHOLD } from '@modules/shared-types';
import type { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useContentPlanFlowReadiness } from '../../hooks/useContentPlanFlowReadiness';
import { usePillars } from '../../hooks/usePillars';
import { TopicPillarService } from '../../services/TopicPillarService';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { GenerateTopicsDialog } from '../GenerateTopicsDialog';
import { GenerationReviewDialog } from '../generations/GenerationReviewDialog';
import type { GenerationRunSummary } from '../../hooks/useGenerationRuns';
import {
  buildStripSummary,
  buildWizardStepViews,
  type WizardStepId,
  type WizardStepView,
  type WizardSubTabId,
} from './wizardSteps';
import { WizardStepCard } from './WizardStepCard';
import { TaskProgressBanner } from './TaskProgressBanner';
import { RunAllConfirmDialog } from './RunAllConfirmDialog';
import { RunAllProgressPanel, type RunAllStageAction } from './RunAllProgressPanel';
import { useRunAllOrchestrator, type RunAllStageState } from './useRunAllOrchestrator';

// ─── Feature flag (M3 entry) — simplest module-local pattern: localStorage
//     opt-out; default ON (the page itself is admin/billing-gated). Set
//     `localStorage.contentPlanRunAllEnabled = 'false'` to hide the entry. ───
function isRunAllEnabled(): boolean {
  try {
    return window.localStorage.getItem('contentPlanRunAllEnabled') !== 'false';
  } catch {
    return true;
  }
}

const collapseStorageKey = (projectId: string) => `cp:wizard:collapsed:${projectId}`;

export interface ContentPlanWizardStripProps {
  projectId: string;
  userId: string;
  /** Workspace author (null in overview mode). */
  authorId: string | null;
  isOverview: boolean;
  strategyService: StrategyService;
  themeCatalog?: Array<{ id: string; name: string }>;
  /** EXPLICIT user navigation — performs the same update a sub-tab click does. */
  onOpenSubTab: (subTab: WizardSubTabId) => void;
  /** Opens the page-owned AuthorOnboardingDialog. */
  onOpenOnboarding: () => void;
  /** Opens the page-owned CreateContentStrategyDialog. */
  onOpenCreateStrategy: () => void;
  /** Persistent long-task banner (page-owned so it survives everything). */
  activeTask: { taskId: string; label: string } | null;
  onDismissTask: () => void;
  onTaskLaunched: (taskId: string, label: string) => void;
  /** Increment to force-expand the strip (toast "View progress" CTA). */
  expandNonce?: number;
}

export const ContentPlanWizardStrip: React.FC<ContentPlanWizardStripProps> = ({
  projectId,
  userId,
  authorId,
  isOverview,
  strategyService,
  themeCatalog,
  onOpenSubTab,
  onOpenOnboarding,
  onOpenCreateStrategy,
  activeTask,
  onDismissTask,
  onTaskLaunched,
  expandNonce = 0,
}) => {
  const apiClient = useMemo(() => new ContentPlanApiClient(), []);
  const pillarService = useMemo(() => new TopicPillarService(), []);

  const {
    readiness, counts, effectiveAuthorId, loading,
  } = useContentPlanFlowReadiness(projectId, authorId);
  const views = useMemo(() => buildWizardStepViews(readiness.steps), [readiness.steps]);
  const summary = useMemo(() => buildStripSummary(views), [views]);

  // Draft pillars (for the pillar step primary action label / handler).
  const { pillars } = usePillars(projectId);
  const draftPillars = useMemo(
    () => pillars.filter((pillar) => pillar.isDraft
      && (!effectiveAuthorId
        || (Array.isArray(pillar.authorIds) && pillar.authorIds.includes(effectiveAuthorId)))),
    [pillars, effectiveAuthorId],
  );

  // ── Collapse state (persisted per session; auto-collapse when complete) ──
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(collapseStorageKey(projectId)) === '1';
    } catch {
      return false;
    }
  });
  const autoCollapseApplied = useRef(false);
  useEffect(() => {
    if (loading || autoCollapseApplied.current) return;
    autoCollapseApplied.current = true;
    try {
      if (sessionStorage.getItem(collapseStorageKey(projectId)) === null && readiness.ready) {
        // Mature project: strip costs ~0px until explicitly expanded (open Q1).
        setCollapsed(true);
      }
    } catch {
      // Storage unavailable — keep the in-memory default.
    }
  }, [loading, readiness.ready, projectId]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(collapseStorageKey(projectId), next ? '1' : '0');
      } catch {
        // Session storage can be disabled; state stays in memory.
      }
      return next;
    });
  }, [projectId]);

  // Toast "View progress" CTA → force-expand (explicit user click).
  const lastExpandNonce = useRef(expandNonce);
  useEffect(() => {
    if (expandNonce !== lastExpandNonce.current) {
      lastExpandNonce.current = expandNonce;
      setCollapsed(false);
    }
  }, [expandNonce]);

  // ── Step selection (wizard-internal; never navigates) ──
  const [userSelectedStep, setUserSelectedStep] = useState<WizardStepId | null>(null);
  const selectedStep: WizardStepView | undefined = useMemo(() => {
    const explicit = userSelectedStep
      ? views.find((view) => view.id === userSelectedStep)
      : undefined;
    return explicit ?? views.find((view) => view.active) ?? views[views.length - 1];
  }, [views, userSelectedStep]);

  // ── Step-card primary actions ──
  const [pillarsBusy, setPillarsBusy] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [genTopicsOpen, setGenTopicsOpen] = useState(false);

  const statusLineFor = useCallback((step: WizardStepView): string => {
    if (step.id === 'research') {
      return 'Optional: run topic research from the Themes sub-tab to enrich generation. Never blocks the flow.';
    }
    const r = step.readiness;
    if (!r) return '';
    if (!r.ok && r.detail) return r.detail;
    switch (step.id) {
      case 'author': return `${counts.authorCount} author(s) — default author resolved.`;
      case 'platforms': return `${counts.platformCount} platform(s) enabled.`;
      case 'pillars': return `${counts.confirmedPillarCount} confirmed pillar(s)${draftPillars.length > 0 ? ` · ${draftPillars.length} draft(s) awaiting confirmation` : ''}.`;
      case 'backlog': return `${counts.readyTopicCount} of ${CONTENT_PLAN_BACKLOG_READY_THRESHOLD} ready topics in the backlog.`;
      case 'strategy': return `${counts.strategyCount} content strateg${counts.strategyCount === 1 ? 'y' : 'ies'}.`;
      case 'publications': return `${counts.publicationCount} calendar publication(s) on the newest strategy.`;
      case 'posts': return `${counts.postCount} canonical post(s).`;
      case 'assets': return r.detail ?? 'Per-post asset readiness is inspected on each post.';
      default: return r.detail ?? '';
    }
  }, [counts, draftPillars.length]);

  const primaryLabelFor = useCallback((step: WizardStepView): string => {
    switch (step.primaryAction) {
      case 'onboard-author': return 'Setup author profile';
      case 'pillars':
        return draftPillars.length > 0
          ? `Confirm all drafts (${draftPillars.length})`
          : 'Generate pillars from profile';
      case 'generate-topics': return 'Generate topics';
      case 'create-strategy': return 'Create strategy';
      case 'open-subtab':
      default:
        return `Open ${step.subTabLabel}`;
    }
  }, [draftPillars.length]);

  /** Warn before re-running a step whose downstream output exists. */
  const confirmRerunIfDownstream = useCallback((step: WizardStepView): boolean => {
    const hasDownstream = (step.id === 'backlog' || step.id === 'pillars')
      && counts.strategyCount > 0;
    if (!step.complete && !hasDownstream) return true;
    if (!hasDownstream) return true;
    return window.confirm(
      'Downstream output exists (strategy and/or posts). Re-running this step can shift what later stages were built on. Continue?',
    );
  }, [counts.strategyCount]);

  const handlePrimary = useCallback((step: WizardStepView) => {
    setStepError(null);
    switch (step.primaryAction) {
      case 'onboard-author':
        onOpenOnboarding();
        return;
      case 'pillars': {
        if (draftPillars.length > 0) {
          setPillarsBusy(true);
          void (async () => {
            try {
              await Promise.all(
                draftPillars.map((pillar) => pillarService.confirm(projectId, pillar.id)),
              );
            } catch (e) {
              setStepError((e as Error).message);
            } finally {
              setPillarsBusy(false);
            }
          })();
          return;
        }
        if (!effectiveAuthorId) {
          setStepError('No author resolved yet — complete the Author step first.');
          return;
        }
        if (!confirmRerunIfDownstream(step)) return;
        setPillarsBusy(true);
        void (async () => {
          try {
            const response = await apiClient.authorAnalyze({
              projectId,
              userId,
              existingAuthorId: effectiveAuthorId,
              scope: 'pillars',
            });
            if (!response.success || !response.taskId) {
              throw new Error(response.error ?? 'Pillar generation failed to start.');
            }
            onTaskLaunched(response.taskId, 'Generate pillars');
          } catch (e) {
            setStepError((e as Error).message);
          } finally {
            setPillarsBusy(false);
          }
        })();
        return;
      }
      case 'generate-topics':
        if (!confirmRerunIfDownstream(step)) return;
        setGenTopicsOpen(true);
        return;
      case 'create-strategy':
        onOpenCreateStrategy();
        return;
      case 'open-subtab':
      default:
        onOpenSubTab(step.subTab);
    }
  }, [
    apiClient, confirmRerunIfDownstream, draftPillars, effectiveAuthorId, onOpenCreateStrategy,
    onOpenOnboarding, onOpenSubTab, onTaskLaunched, pillarService, projectId, userId,
  ]);

  // ── M3 Run-all ──
  const runAllEnabled = isRunAllEnabled();
  const [runAllDialogOpen, setRunAllDialogOpen] = useState(false);
  const [runAllActionError, setRunAllActionError] = useState<string | null>(null);
  const runAll = useRunAllOrchestrator({
    projectId,
    userId,
    authorId: effectiveAuthorId,
    counts,
    onOpenOnboarding,
  });
  const runAllVisible = runAll.active || runAll.stages.some((s) => s.status !== 'pending');

  const actionsForStage = useCallback((stage: RunAllStageState): RunAllStageAction[] => {
    if (stage.status === 'error') {
      return [{ label: 'Retry', onClick: () => runAll.retryStage(stage.id) }];
    }
    if (stage.status !== 'paused') return [];
    switch (stage.pauseKind) {
      case 'needs-author':
        return [{ label: 'Setup author profile', onClick: onOpenOnboarding }];
      case 'confirm-pillars': {
        const actions: RunAllStageAction[] = [];
        if (draftPillars.length > 0) {
          actions.push({
            label: `Confirm all drafts (${draftPillars.length})`,
            onClick: () => {
              setRunAllActionError(null);
              void runAll.confirmAllDraftPillars().catch((e) => {
                setRunAllActionError((e as Error).message);
              });
            },
          });
        }
        actions.push({
          label: 'Open Creator',
          onClick: () => onOpenSubTab('creator'),
          secondary: true,
        });
        return actions;
      }
      case 'review-topics': {
        const actions: RunAllStageAction[] = [];
        if (stage.generationId) {
          actions.push({ label: 'Review drafts', onClick: () => runAll.openReview(stage) });
        }
        actions.push({
          label: 'Generate more topics',
          onClick: () => runAll.retryStage('topics'),
          secondary: true,
        });
        return actions;
      }
      case 'review-posts': {
        const actions: RunAllStageAction[] = [];
        if (stage.generationId) {
          actions.push({ label: 'Review post drafts', onClick: () => runAll.openReview(stage) });
        }
        actions.push({
          label: 'Open Strategies',
          onClick: () => onOpenSubTab('strategies'),
          secondary: true,
        });
        return actions;
      }
      default:
        return [];
    }
  }, [draftPillars.length, onOpenOnboarding, onOpenSubTab, runAll]);

  const reviewRunStub: GenerationRunSummary | null = runAll.reviewTarget
    ? { id: runAll.reviewTarget.generationId, status: 'completed' }
    : null;

  // ── Render ──
  const primaryDisabledReason = isOverview
    ? 'Read-only overview — pick a single author to act on flow steps.'
    : undefined;

  return (
    <Paper
      variant="outlined"
      data-testid={CONTENT_PLAN.WIZARD_STRIP}
      sx={{ p: 1.5, mb: 2 }}
    >
      {/* Header — always visible (collapsed bar). */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          size="small"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand content plan flow' : 'Collapse content plan flow'}
          data-testid={CONTENT_PLAN.WIZARD_STRIP_TOGGLE}
        >
          {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
        <Typography
          variant="subtitle2"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          data-testid={CONTENT_PLAN.WIZARD_STRIP_SUMMARY}
        >
          {loading ? 'Content plan flow — checking readiness…' : summary}
        </Typography>
        {readiness.ready && !loading && (
          <Chip size="small" color="success" variant="outlined" label="Flow ready" />
        )}
        {runAllEnabled && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlaylistPlayIcon fontSize="small" />}
            disabled={isOverview || runAll.active || loading}
            onClick={() => setRunAllDialogOpen(true)}
            data-testid={CONTENT_PLAN.RUNALL_BUTTON}
          >
            Run all
          </Button>
        )}
      </Stack>

      {/* Persistent task banner — OUTSIDE the collapse region so a running
          task stays visible even with the strip collapsed (LT1). */}
      {activeTask && (
        <Box sx={{ mt: 1.5 }}>
          <TaskProgressBanner
            projectId={projectId}
            taskId={activeTask.taskId}
            label={activeTask.label}
            onDismiss={onDismissTask}
          />
        </Box>
      )}

      {!collapsed && (
        <Box sx={{ mt: 1.5 }}>
          {/* 9-node horizontal spine (skeleton while subscriptions warm up). */}
          {loading ? (
            <Stack direction="row" spacing={1}>
              {Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" width={96} height={40} />
              ))}
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ overflowX: 'auto', pb: 0.5 }}
            >
              {views.map((step) => {
                const isSelected = selectedStep?.id === step.id;
                return (
                  <Tooltip
                    key={step.id}
                    title={step.locked ? (step.lockReason ?? '') : statusLineFor(step)}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setUserSelectedStep(step.id)}
                      aria-disabled={step.locked}
                      aria-current={step.active ? 'step' : undefined}
                      data-testid={CONTENT_PLAN.wizardStepNode(step.id)}
                      data-step-state={
                        step.complete ? 'complete' : step.locked ? 'locked' : step.active ? 'active' : 'available'
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 1,
                        cursor: 'pointer',
                        flexShrink: 0,
                        font: 'inherit',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderStyle: step.advisory ? 'dashed' : 'solid',
                        opacity: step.locked ? 0.55 : 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {step.complete ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : step.locked ? (
                        <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            width: 16,
                            textAlign: 'center',
                            fontWeight: step.active ? 700 : 400,
                            color: step.active ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {step.position}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: step.active || isSelected ? 600 : 400,
                          color: step.locked ? 'text.disabled' : 'text.primary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {step.advisory && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          opt
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          )}

          {/* Expanded step card for the selected step. */}
          {selectedStep && (
            <WizardStepCard
              step={selectedStep}
              statusLine={statusLineFor(selectedStep)}
              primaryLabel={primaryLabelFor(selectedStep)}
              primaryBusy={selectedStep.id === 'pillars' && pillarsBusy}
              primaryDisabledReason={primaryDisabledReason}
              onPrimary={() => handlePrimary(selectedStep)}
              onOpenSubTab={() => onOpenSubTab(selectedStep.subTab)}
              onSelectStep={(stepId) => setUserSelectedStep(stepId)}
              error={stepError}
              onClearError={() => setStepError(null)}
            />
          )}

          {/* M3 Run-all staged progress (in-page aggregate surface). */}
          {runAllVisible && (
            <>
              {runAllActionError && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                    {runAllActionError}
                  </Typography>
                </Box>
              )}
              <RunAllProgressPanel
                stages={runAll.stages}
                active={runAll.active}
                currentPhaseName={runAll.currentPhaseName}
                actionsForStage={actionsForStage}
                onStop={runAll.stop}
                onDismiss={runAll.dismiss}
              />
            </>
          )}
        </Box>
      )}

      {/* Wizard-owned dialogs. */}
      <RunAllConfirmDialog
        open={runAllDialogOpen}
        onClose={() => setRunAllDialogOpen(false)}
        onConfirm={(options) => {
          setRunAllDialogOpen(false);
          setCollapsed(false);
          runAll.start(options);
        }}
      />

      <GenerateTopicsDialog
        open={genTopicsOpen}
        onClose={() => setGenTopicsOpen(false)}
        projectId={projectId}
        userId={userId}
        authorId={effectiveAuthorId ?? ''}
        strategyService={strategyService}
        themeCatalog={themeCatalog ?? []}
        onSuccess={({ taskId }) => onTaskLaunched(taskId, 'Generate topics')}
      />

      {/* Run-all review checkpoints reuse the EXISTING review dialog. */}
      <GenerationReviewDialog
        open={Boolean(runAll.reviewTarget)}
        onClose={runAll.closeReview}
        projectId={projectId}
        entity={runAll.reviewTarget?.entity ?? 'topic'}
        run={reviewRunStub}
        onApply={runAll.applyReview}
        renderItem={(draft) => {
          const data = draft.data as Record<string, unknown>;
          const title = (data.angle as string) ?? (data.hook as string) ?? (data.title as string) ?? draft.id;
          const detail = (data.summary as string) ?? (data.keyMessage as string) ?? (data.body as string) ?? '';
          return (
            <Stack spacing={0.25}>
              <Typography variant="subtitle2">{String(title)}</Typography>
              {detail && (
                <Typography variant="caption" color="text.secondary">
                  {String(detail).slice(0, 220)}
                </Typography>
              )}
            </Stack>
          );
        }}
      />
    </Paper>
  );
};

export default ContentPlanWizardStrip;
